import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, TargetIcon, TrendingUpIcon, CpuIcon, GraduationCapIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Container, StatBlock, Reveal } from '../components/ui/Primitives';
import { HeroArcs, PlatformMock } from '../components/sections/HeroVisual';
import { ExplainerVideo } from '../components/sections/ExplainerVideo';
import { SERVICE_LINES, METHOD_STEPS, SECTORS } from '../data/services';
import { ARTICLES } from '../data/insights';
import { RESOURCES } from '../data/resources';
const CAPABILITY_CARDS = [{
  icon: TargetIcon,
  title: 'Market decisions',
  detail: 'Size a market from the bottom up, name the competitors and their terms, and cost the entry route.',
  to: '/services/intelligence'
}, {
  icon: TrendingUpIcon,
  title: 'Demand and revenue',
  detail: 'Choose the segments, price the offer and build the pipeline that makes demand arrive predictably.',
  to: '/services/growth'
}, {
  icon: CpuIcon,
  title: 'AI adoption',
  detail: 'Put AI where it moves a number your board already watches, with the governance written first.',
  to: '/services/ai'
}, {
  icon: GraduationCapIcon,
  title: 'Capability building',
  detail: 'Hand over the method, the models and the training so the work continues without us.',
  to: '/augmented-intelligence'
}];
export function Home() {
  const reduced = useReducedMotion();
  const featured = ARTICLES.slice(0, 3);
  const playbook = RESOURCES[0];
  return <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-ink" data-id="element-613" data-mp="28618dlbk4mpv">
        <HeroArcs className="pointer-events-none absolute -right-10 top-0 h-[420px] w-[420px] opacity-90" data-id="element-614" data-mp="23acbjpqx9jx9" />
        <Container className="relative py-16 lg:py-24" data-id="element-615" data-mp="1z6wfovmofnlm">
          <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.05fr]" data-id="element-616" data-mp="1u7t936r1t9bj">
            <motion.div initial={reduced ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-617" data-mp="1kkqnlgkg8gf0">
              <h1 className="font-display text-[40px] leading-[1.06] text-white sm:text-[56px] lg:text-[64px]" data-id="element-618" data-mp="1amt04vaeynng">
                Empowering the organisations shaping African markets
              </h1>
              <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-white/72 sm:text-[19px]" data-id="element-619" data-mp="23bgncweq3n3n">
                Market intelligence, data led growth and augmented intelligence, delivered as evidence you can
                take into the room where the decision is made.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-620" data-mp="1yk8fw5wyebt4">
                <Button to="/contact" size="lg" shape="pill" data-id="element-621" data-mp="1yif8goeinnk2">
                  Book a discovery call
                </Button>
                <Button to="/assessment" variant="reversed" size="lg" shape="pill" data-id="element-622" data-mp="2r0j5s6h6ljz7">
                  Take the AI readiness assessment
                </Button>
              </div>
            </motion.div>

            <motion.div className="flex justify-center lg:justify-end" initial={reduced ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: 'easeOut',
            delay: 0.12
          }} data-id="element-623" data-mp="amz7d3xferhv">
              <PlatformMock data-id="element-624" data-mp="3ssc9uxarocy0" />
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Capability cards */}
      <section aria-labelledby="capabilities" className="border-t border-white/10 bg-ink" data-id="element-625" data-mp="3nu12ct7y2eiy">
        <Container className="py-16 lg:py-24" data-id="element-626" data-mp="5nwfkl6gihru">
          <h2 id="capabilities" className="max-w-4xl font-display text-[30px] leading-tight text-white sm:text-[42px]" data-id="element-627" data-mp="5oxook3wwu6x">
            Intelligence, method and technology for:
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-628" data-mp="1c3qd7dfl2z9p">
            {CAPABILITY_CARDS.map((card, index) => {
            const Icon = card.icon;
            return <Reveal key={card.title} delay={index * 0.06} className="h-full" data-id="element-629" data-mp="27g9chtf8l1o4" data-mpi={index}>
                  <Link to={card.to} className="group flex h-full flex-col border border-white/20 p-7 transition-colors duration-200 hover:border-gold" data-id="element-630" data-mp="2cf2eaol5v48g">
                    <Icon className="h-8 w-8 text-white" aria-hidden="true" strokeWidth={1.4} data-id="element-631" data-mp="2m8t1zr9gran3" />
                    <h3 className="mt-6 font-display text-[24px] leading-snug text-white" data-id="element-632" data-mp="2qawp5d5hw3o7">{card.title}</h3>
                    <p className="mt-4 text-[15px] leading-relaxed text-white/70" data-id="element-633" data-mp="302kb4yja8sia">{card.detail}</p>
                    <span className="mt-auto inline-flex items-center gap-2 pt-7 text-[14px] font-medium text-gold" data-id="element-634" data-mp="351nhqnewv6sd">
                      Learn more
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-635" data-mp="3a21eh9b2aub3" />
                    </span>
                  </Link>
                </Reveal>;
          })}
          </div>
        </Container>
      </section>

      {/* Animated explainer */}
      <section aria-labelledby="explainer" className="bg-white" data-id="element-636" data-mp="tfcw3kvlvn5i">
        <Container className="py-16 lg:py-24" data-id="element-637" data-mp="ofgu1r6gy5sl">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-638" data-mp="ogrvhkcfz1gj">
            <div className="max-w-2xl" data-id="element-639" data-mp="1m1xnumfzu19l">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-640" data-mp="1m51z23rvlx9r">Watch</p>
              <h2 id="explainer" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-641" data-mp="1h5z4jd9iv9g1">
                How Cavalla Solution works
              </h2>
              <p className="mt-5 text-[17px] text-body" data-id="element-642" data-mp="17djmyh250mvo">
                Half a minute on how an engagement runs, from scattered evidence to a decision somebody owns.
              </p>
            </div>
          </div>
          <div className="mt-10" data-id="element-643" data-mp="200nky22tc0xy">
            <ExplainerVideo data-id="element-644" data-mp="3uauh0bv1zlnx" />
          </div>
        </Container>
      </section>

      {/* Method */}
      <section aria-labelledby="method" className="bg-white" data-id="element-645" data-mp="26jpzfkae6os">
        <Container className="py-16 lg:py-24" data-id="element-646" data-mp="28w7jrm4uf1b">
          <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-647" data-mp="3klkm6o8trow4">
            <div className="lg:sticky lg:top-32 lg:self-start" data-id="element-648" data-mp="3pjvx72auw5fx">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-649" data-mp="3piayd0hie0c9">Our method</p>
              <h2 id="method" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-650" data-mp="3ala8l2ivofgf">
                Four steps, run on every engagement
              </h2>
              <p className="mt-5 max-w-md text-[17px] text-body" data-id="element-651" data-mp="31n88qqavrsuq">
                Augmented intelligence is the deliberate pairing of machine analysis with human judgement,
                arranged so a person stays accountable for the decision.
              </p>
              <Button to="/augmented-intelligence" variant="secondary" shape="pill" className="mt-8" data-id="element-652" data-mp="14u6h6lyf2dln">
                Walk through the method
              </Button>
            </div>

            <ol className="divide-y divide-line border-t border-line" data-id="element-653" data-mp="19ygxze3ar5xc">
              {METHOD_STEPS.map((step, index) => <li key={step.id} className="grid gap-4 py-8 sm:grid-cols-[64px_1fr]" data-id="element-654" data-mp="1nq5ahihgjweu" data-mpi={index}>
                  <span className="font-display text-[26px] leading-none text-gold" data-id="element-655" data-mp="1iynbv12wq7x1">0{index + 1}</span>
                  <div data-id="element-656" data-mp="1xnjprernaf5k">
                    <h3 className="font-display text-[24px] leading-snug text-ink" data-id="element-657" data-mp="1xmrvc79sangv">{step.title}</h3>
                    <p className="mt-3 max-w-prose text-[16px] text-body" data-id="element-658" data-mp="1so6vwa2cw7th">{step.summary}</p>
                    <p className="mt-4 text-[13px] uppercase tracking-[0.12em] text-muted" data-id="element-659" data-mp="26nq2gyfirb3o">
                      You receive: <span className="normal-case tracking-normal text-body" data-id="element-660" data-mp="22oh7jq8qt76j">{step.output}</span>
                    </p>
                  </div>
                </li>)}
            </ol>
          </div>
        </Container>
      </section>

      {/* Statistics */}
      <section aria-labelledby="numbers" className="bg-mist" data-id="element-661" data-mp="wul9ac93kaqc">
        <Container className="py-16 lg:py-20" data-id="element-662" data-mp="ws8ta4mwgafx">
          <h2 id="numbers" className="font-display text-[28px] leading-tight text-ink sm:text-[36px]" data-id="element-663" data-mp="11sd2rape44wn">
            The market we work in, in figures
          </h2>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-664" data-mp="1algjpsz8uzw1">
            <Reveal data-id="element-665" data-mp="1ankm26hhvqu7"><StatBlock value={16.5} decimals={1} prefix="$" suffix="bn" label="Africa AI market by 2030" source="Market forecast, 2025" data-id="element-666" data-mp="1anavagomlfng" /></Reveal>
            <Reveal delay={0.05} data-id="element-667" data-mp="itimtye6wyew"><StatBlock value={2.9} decimals={1} suffix="x" label="Growth in enterprise AI spend since 2023" source="Cavalla analysis, 2026" data-id="element-668" data-mp="iv3i5q8bwldt" /></Reveal>
            <Reveal delay={0.1} data-id="element-669" data-mp="ruqi07letj4r"><StatBlock value={61} suffix="%" label="Of programmes stall on data foundations, not models" source="Cavalla client review, 2026" data-id="element-670" data-mp="rv8n7tq1v7ot" /></Reveal>
            <Reveal delay={0.15} data-id="element-671" data-mp="1yd55srlh2s4j"><StatBlock value={10} label="Priority sectors under continuous coverage" source="Cavalla sector coverage" data-id="element-672" data-mp="23a5cn4q5bywp" /></Reveal>
          </div>
        </Container>
      </section>

      {/* Service lines */}
      <section aria-labelledby="lines" className="bg-white" data-id="element-673" data-mp="28f97zk088l13">
        <Container className="py-16 lg:py-24" data-id="element-674" data-mp="28fr8wtxu3nfc">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-675" data-mp="23nr2qjr67yab">
            <div className="max-w-2xl" data-id="element-676" data-mp="1zmnqs8c8umed">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-677" data-mp="1zlmfq3v89zwn">Three lines</p>
              <h2 id="lines" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-678" data-mp="1uods3nrb93o0">
                Start with the question you actually have
              </h2>
            </div>
            <Button to="/services" variant="secondary" shape="pill" size="sm" data-id="element-679" data-mp="1kq6ijpzcpnw1">
              How we work
            </Button>
          </div>

          <div className="mt-12 grid gap-px bg-line lg:grid-cols-3" data-id="element-680" data-mp="3f3frngnzj7dr">
            {SERVICE_LINES.map(line => <article key={line.id} className="flex flex-col bg-white p-8" data-id="element-681" data-mp="3b242fc90wo63">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-682" data-mp="3azzzoxmv1v7e">{line.short}</p>
                <h3 className="mt-4 font-display text-[27px] leading-tight text-ink" data-id="element-683" data-mp="8n9rb4t2u2kt">{line.name}</h3>
                <p className="mt-4 text-[16px] text-body" data-id="element-684" data-mp="mmivt77x788y">{line.promise}</p>
                <ul className="mt-6 space-y-2.5 text-[14px] text-muted" data-id="element-685" data-mp="mkohibr5medu">
                  {line.subServices.map(sub => <li key={sub.name} className="flex gap-2.5" data-id="element-686" data-mp="3q381ys3lcfy3">
                      <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold" data-id="element-687" data-mp="3q1ewvh8mm3s7" />
                      {sub.name}
                    </li>)}
                </ul>
                <div className="mt-auto pt-8" data-id="element-688" data-mp="3pzu32zg4mmf">
                  <Link to={`/services/${line.id}`} className="inline-flex items-center gap-2 text-[15px] font-medium text-royal underline-offset-4 hover:underline" data-id="element-689" data-mp="3v2aw3hi191ra">
                    Explore this line
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-690" data-mp="1hvuu57p1nwse" />
                  </Link>
                </div>
              </article>)}
          </div>
        </Container>
      </section>

      {/* Sectors */}
      <section aria-labelledby="sectors" className="surface-dark" data-id="element-691" data-mp="18vzeapkjndm1">
        <Container className="py-16 lg:py-20" data-id="element-692" data-mp="1cwrn5hpi7ghs">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]" data-id="element-693" data-mp="1cxky6uvhws5q">
            <div data-id="element-694" data-mp="20mm1y4tdvf4o">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-695" data-mp="20m42z0fc6mt2">Coverage</p>
              <h2 id="sectors" className="mt-4 font-display text-[30px] leading-tight text-white sm:text-[40px]" data-id="element-696" data-mp="25ke3lqjtyfm2">
                Ten priority sectors, tracked continuously
              </h2>
              <p className="mt-5 max-w-md text-[16px] text-white/70" data-id="element-697" data-mp="1wjzc29g9w4a4">
                We hold a standing view of demand, policy and buyer behaviour in each of these, refreshed monthly
                rather than assembled when a client asks.
              </p>
            </div>
            <ul className="grid grid-cols-2 gap-px self-start bg-white/15 sm:grid-cols-3" data-id="element-698" data-mp="2muhyglxrd9vi">
              {SECTORS.map(sector => <li key={sector} className="bg-[#0d2c53] px-5 py-6 text-[16px] text-white/85" data-id="element-699" data-mp="2hwzocs808olx">
                  {sector}
                </li>)}
            </ul>
          </div>
        </Container>
      </section>

      {/* Insights */}
      <section aria-labelledby="insights" className="bg-white" data-id="element-700" data-mp="244a0ei8x19m6">
        <Container className="py-16 lg:py-24" data-id="element-701" data-mp="245uuk6rbiqji">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-702" data-mp="1z5gkyka2j8jd">
            <div data-id="element-703" data-mp="1z7jma0go8o3e">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-704" data-mp="1u5u84by5jub3">Insights</p>
              <h2 id="insights" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-705" data-mp="1q4s9nxc27ma6">
                Analysis for people who have to act on it
              </h2>
            </div>
            <Button to="/insights" variant="secondary" shape="pill" size="sm" data-id="element-706" data-mp="3ki0962azcm25">
              All insights
            </Button>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-3" data-id="element-707" data-mp="16aignyishi00">
            {featured.map((article, index) => <Reveal key={article.slug} delay={index * 0.06} className="h-full" data-id="element-708" data-mp="1b7r1k6vbo04y" data-mpi={index}>
                <article className="group flex h-full flex-col" data-id="element-709" data-mp="1g13omckcryvl">
                  <Link to={`/insights/${article.slug}`} className="block overflow-hidden" data-id="element-710" data-mp="1k2f77y4t7jua">
                    <img src={article.heroImage} alt={article.heroAlt} loading="lazy" className="aspect-[3/2] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" data-id="element-711" data-mp="ncg1ebg1mzfe" />
                  </Link>
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold" data-id="element-712" data-mp="11cgzxk8u5urp">
                    {article.sectors[0]} · {article.readTime} minute read
                  </p>
                  <h3 className="mt-3 font-display text-[22px] leading-snug text-ink" data-id="element-713" data-mp="2cpjeyavaec9r">
                    <Link to={`/insights/${article.slug}`} className="underline-offset-4 hover:underline" data-id="element-714" data-mp="2baqhv91aiq45">
                      {article.title}
                    </Link>
                  </h3>
                  <p className="mt-3 text-[15px] text-body" data-id="element-715" data-mp="2kalvrlm8cz7v">{article.excerpt}</p>
                </article>
              </Reveal>)}
          </div>
        </Container>
      </section>

      {/* eBook */}
      <section aria-labelledby="ebook" className="bg-champagne" data-id="element-716" data-mp="2u78hecmdruay">
        <Container className="py-16 lg:py-20" data-id="element-717" data-mp="2p9q306plgmvk">
          <div className="grid items-center gap-12 lg:grid-cols-[0.7fr_1.3fr]" data-id="element-718" data-mp="2p8x57v8m57pk">
            <img src={playbook.cover} alt={playbook.coverAlt} loading="lazy" className="mx-auto w-full max-w-[300px]" data-id="element-719" data-mp="33xbasdxeza9t" />
            <div data-id="element-720" data-mp="3cy9gr1rexrv1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-721" data-mp="3czsw8q58vyvr">Free eBook</p>
              <h2 id="ebook" className="mt-4 font-display text-[30px] leading-tight text-ink sm:text-[40px]" data-id="element-722" data-mp="381j12fjisya5">
                {playbook.title}
              </h2>
              <p className="mt-5 max-w-xl text-[17px] text-body" data-id="element-723" data-mp="fyhf2ugc3z3">{playbook.summary}</p>
              <ul className="mt-6 space-y-2.5 text-[15px] text-body" data-id="element-724" data-mp="3ilbiqsw23jdn">
                {playbook.whatIsInside.slice(0, 3).map(item => <li key={item} className="flex gap-3" data-id="element-725" data-mp="3mlbwebbbzqjn">
                    <span aria-hidden="true" className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-gold" data-id="element-726" data-mp="38vi93xfh5ec1" />
                    {item}
                  </li>)}
              </ul>
              <Button to={`/resources/${playbook.slug}`} size="lg" shape="pill" className="mt-8" data-id="element-727" data-mp="2yx2jy000cf56">
                Download the playbook
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Client proof */}
      <section aria-labelledby="proof" className="bg-white" data-id="element-728" data-mp="2pww80ijyjn84">
        <Container className="py-16 lg:py-20" data-id="element-729" data-mp="2uvz2j92bab1u">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-730" data-mp="2uunxz711kz9o">
            <div className="max-w-2xl" data-id="element-731" data-mp="10gaca7gs98ki">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-732" data-mp="10h3ecv4sr9wb">Client words</p>
              <h2 id="proof" className="mt-4 font-display text-[30px] leading-tight text-ink sm:text-[40px]" data-id="element-733" data-mp="qpffwa9toslc">
                We publish results, not promises
              </h2>
              <p className="mt-5 text-[16px] text-body" data-id="element-734" data-mp="mlr7xqha3uzk">
                Case studies from our current engagements will appear here as each one completes, with the client
                named and the numbers agreed.
              </p>
            </div>
          </div>
          <div className="mt-10 grid gap-px bg-line sm:grid-cols-3" data-id="element-735" data-mp="1acdd99zwf15v">
            {['Banking, West Africa', 'Government, East Africa', 'NGO, multi country'].map(label => <div key={label} className="bg-mist p-8" data-id="element-736" data-mp="3v46ws76brpzw">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-737" data-mp="3v1kno33sd2fk">{label}</p>
                <p className="mt-5 font-display text-[20px] leading-snug text-muted/75" data-id="element-738" data-mp="3q6x9oy6erqf8">
                  Case study in preparation
                </p>
                <p className="mt-3 text-[14px] text-muted" data-id="element-739" data-mp="2amo81m2oxrmw">Published on completion.</p>
              </div>)}
          </div>
        </Container>
      </section>

      {/* Closing call to action */}
      <section className="relative isolate overflow-hidden bg-ink" data-id="element-740" data-mp="2jeq8vlgo99xp">
        <HeroArcs className="pointer-events-none absolute -right-16 -top-8 h-[300px] w-[300px] opacity-70" data-id="element-741" data-mp="2odj9weerv8gz" />
        <Container className="relative py-16 lg:py-20" data-id="element-742" data-mp="2tbbeo90imp2l">
          <div className="flex flex-col items-start justify-between gap-9 lg:flex-row lg:items-center" data-id="element-743" data-mp="2tdxko47rcwip">
            <div className="max-w-xl" data-id="element-744" data-mp="2y9cxqtxxyoe2">
              <h2 className="font-display text-[30px] leading-tight text-white sm:text-[40px]" data-id="element-745" data-mp="32d2ewycae3p2">
                Tell us the decision you are facing
              </h2>
              <p className="mt-5 text-[17px] text-white/72" data-id="element-746" data-mp="17yrv54ajtzq5">
                A discovery call lasts thirty minutes. You leave it with a view on whether we are the right firm
                for the problem, and a first read on how we would approach it.
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row" data-id="element-747" data-mp="k1lpdvef6tjy">
              <Button to="/contact" size="lg" shape="pill" data-id="element-748" data-mp="xubazyq1dwgj">
                Book a discovery call
              </Button>
              <Button to="/services" variant="reversed" size="lg" shape="pill" data-id="element-749" data-mp="tvlrrpibhkoz">
                Explore our services
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>;
}